/**
 * The gap targets while arranging, on both arrange pages (a report's open tab and the Site Analytics
 * page, each mounting it as the grid's sibling): a pointer resting on empty space in the card grid,
 * the space the row grid (RowGrid, #4938) leaves beside a shorter card or at the end of a row, shows
 * a dashed "+ Add chart" over that space, and a click opens the Add chart panel set to insert there
 * (the page's openPaletteAt(), Report\Show's and Sites\Analytics' alike).
 *
 * The gaps are read off the grid as the browser laid it out, never predicted: each card's cell is
 * its box against the grid's own columns and rows (grid-template-columns, grid-auto-rows and the
 * gaps, off the grid's computed style), its size the `span N` its classes give it. The grid places
 * cards by CSS grid's sparse auto-placement, so their places run in reading order in the DOM's
 * order: the index a gap inserts at is how many cards start before its top-left cell, and the card
 * then lands at the first place after the card before it where it fits, which is the gap when it is
 * no wider than the gap (the server gives it the gap's width, clamped to its type's minimum).
 *
 * Everything is measured on the pointer's move, so a resize, a reflow or a render never leaves a
 * stale target. Pointer-only, as the brief for it asks: the element is aria-hidden and out of the tab
 * order, and the dashed tile after the grid ("Add a chart to <tab>", "Add a chart to the page") is the
 * keyboard's way in.
 *
 * Nothing shows below lg (the grid is not twelve columns there), during a drag (from the arrange
 * module's `arrange-grab` to the drag's end) or a resize (`sa-resizing` on the body), while a card's
 * menu is open, or where anything else is under the pointer (a card, a menu hanging over the gap).
 */
const LG_QUERY = '(min-width: 1024px)';
const COLUMNS = 12;
/** The smallest space shown: a Big number's or a key figure's minimum width (minimumSpan()) and two rows. */
const MIN_SPAN = 2;
const MIN_ROWS = 2;

export default function ReportGridGaps() {
    let wrapper = null;
    let lg = null;
    let frame = null;
    let pointer = null;
    let listeners = [];
    let resizeObserver = null;

    return {
        /** The gap under the pointer, in px against the wrapper, with what a click posts; or null. */
        gap: null,

        /** A grab is being held or dragged (arrange-grab until the pointer or the drag lets go). */
        isHeld: false,

        init() {
            wrapper = this.$el.parentElement;
            lg = window.matchMedia(LG_QUERY);

            const on = (target, type, handler, options) => {
                target.addEventListener(type, handler, options);
                listeners.push(() => target.removeEventListener(type, handler, options));
            };

            on(wrapper, 'pointermove', (event) => {
                pointer = { x: event.clientX, y: event.clientY, target: event.target };
                this.schedule();
            });
            on(wrapper, 'pointerleave', () => {
                pointer = null;
                this.gap = null;
            });
            on(window, 'arrange-grab', () => {
                this.isHeld = true;
                this.gap = null;
            });

            const release = () => {
                this.isHeld = false;
            };

            ['pointerup', 'dragend', 'drop'].forEach((type) => on(window, type, release, true));

            // A render or a resize reflows the grid under a pointer that has not moved.
            resizeObserver = new ResizeObserver(() => this.schedule());
            resizeObserver.observe(wrapper);
        },

        destroy() {
            listeners.forEach((remove) => remove());
            listeners = [];
            resizeObserver?.disconnect();

            if (frame !== null) window.cancelAnimationFrame(frame);
        },

        schedule() {
            if (frame !== null) return;

            frame = window.requestAnimationFrame(() => {
                frame = null;
                this.gap = this.gapUnderPointer();
            });
        },

        gapUnderPointer() {
            if (pointer === null || !this.mayShow(pointer.target)) return null;

            const grid = wrapper.querySelector('[data-site-analytics-grid]');
            const layout = grid ? measureGrid(grid) : null;

            if (layout === null) return null;

            const x = pointer.x - layout.left;
            const y = pointer.y - layout.top;

            if (x < 0 || y < 0) return null;

            const column = Math.min(COLUMNS - 1, Math.floor(x / layout.columnStep));
            const row = Math.floor(y / layout.rowStep);
            const gap = freeRects(layout).find((rect) => rect.span >= MIN_SPAN
                && rect.rows >= MIN_ROWS
                && column >= rect.column && column < rect.column + rect.span
                && row >= rect.row && row < rect.row + rect.rows);

            if (!gap) return null;

            const origin = wrapper.getBoundingClientRect();

            return {
                left: layout.left - origin.left + gap.column * layout.columnStep,
                top: layout.top - origin.top + gap.row * layout.rowStep,
                width: gap.span * layout.columnStep - layout.columnGap,
                height: gap.rows * layout.rowStep - layout.rowGap,
                index: insertIndex(layout.cards, gap),
                span: gap.span,
                band: bandOf(layout.cards, gap.row),
                nextTo: neighbourOf(layout, gap),
            };
        },

        /** Whether a target may show at all where the pointer is: see the file's docblock. */
        mayShow(target) {
            if (!lg.matches || this.isHeld || document.body.classList.contains('sa-resizing')) return false;

            const grid = wrapper.querySelector('[data-site-analytics-grid]');

            if (!grid || grid.querySelector('.sa-drag-chosen, .sa-drop-ghost, [aria-expanded="true"]')) return false;

            // Only empty space: the grid itself, its wrapper, or the target already shown.
            return target === grid || target === wrapper || this.$el.contains(target);
        },

        gapStyle() {
            return this.gap === null ? {} : {
                left: `${this.gap.left}px`,
                top: `${this.gap.top}px`,
                width: `${this.gap.width}px`,
                height: `${this.gap.height}px`,
            };
        },

        add() {
            const gap = this.gap;

            if (gap === null) return;

            this.gap = null;
            this.$wire.openPaletteAt(gap.index, gap.span, gap.band, gap.nextTo ?? '');
        },
    };
}

/** "span 4" → 4; anything else → null. */
function spanOf(value) {
    const match = /span\s+(\d+)/.exec(value ?? '');

    return match ? Number(match[1]) : null;
}

/**
 * The grid's cells and every card's place on them, in DOM order, or null when the grid is not the
 * twelve columns edit mode arranges on (below lg).
 */
function measureGrid(grid) {
    const style = window.getComputedStyle(grid);
    const columns = style.gridTemplateColumns.split(' ').filter(Boolean).length;

    if (columns !== COLUMNS) return null;

    const rect = grid.getBoundingClientRect();
    const left = rect.left + (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.paddingLeft) || 0);
    const top = rect.top + (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.paddingTop) || 0);
    const width = grid.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    const columnGap = parseFloat(style.columnGap) || 0;
    const rowGap = parseFloat(style.rowGap) || 0;
    const rowPx = parseFloat(style.gridAutoRows);

    if (!(rowPx > 0) || !(width > 0)) return null;

    const columnStep = (width - columnGap * (COLUMNS - 1)) / COLUMNS + columnGap;
    const rowStep = rowPx + rowGap;
    const cards = [];

    [...grid.children].forEach((element) => {
        if (!element.hasAttribute('data-site-analytics-card')) return;

        const box = element.getBoundingClientRect();

        if (box.width === 0) return;

        const cardStyle = window.getComputedStyle(element);

        cards.push({
            id: element.dataset.siteAnalyticsCard,
            column: Math.max(0, Math.round((box.left - left) / columnStep)),
            row: Math.max(0, Math.round((box.top - top) / rowStep)),
            span: spanOf(cardStyle.gridColumnStart) ?? Math.max(1, Math.round((box.width + columnGap) / columnStep)),
            rows: spanOf(cardStyle.gridRowStart) ?? Math.max(1, Math.round((box.height + rowGap) / rowStep)),
        });
    });

    return { left, top, columnStep, rowStep, columnGap, rowGap, cards };
}

/** Which card, if any, holds each cell, rows down to the lowest card's last. */
function occupancy(cards) {
    const height = cards.reduce((most, card) => Math.max(most, card.row + card.rows), 0);
    const cells = Array.from({ length: height }, () => new Array(COLUMNS).fill(null));

    cards.forEach((card) => {
        for (let row = card.row; row < card.row + card.rows && row < height; row++) {
            for (let column = card.column; column < card.column + card.span && column < COLUMNS; column++) {
                cells[row][column] = card;
            }
        }
    });

    return cells;
}

/**
 * The empty space among the cards as rectangles: each row's runs of free cells, a run joined to the
 * one above it when they cover the same columns. Below the lowest card is not a gap: that is where
 * the end tile adds.
 */
function freeRects(layout) {
    const cells = occupancy(layout.cards);
    const open = new Map();
    const rects = [];

    cells.forEach((line, row) => {
        const runs = [];
        let start = null;

        for (let column = 0; column <= COLUMNS; column++) {
            const isFree = column < COLUMNS && line[column] === null;

            if (isFree && start === null) start = column;

            if (!isFree && start !== null) {
                runs.push([start, column]);
                start = null;
            }
        }

        const next = new Map();

        runs.forEach(([from, to]) => {
            const key = `${from}-${to}`;
            const rect = open.get(key) ?? { column: from, span: to - from, row, rows: 0 };

            rect.rows += 1;
            next.set(key, rect);

            if (!open.has(key)) rects.push(rect);
        });

        open.clear();
        next.forEach((rect, key) => open.set(key, rect));
    });

    return rects;
}

/** How many cards start before the gap's top-left cell, in reading order: the index it inserts at. */
function insertIndex(cards, gap) {
    return cards.filter((card) => card.row < gap.row || (card.row === gap.row && card.column < gap.column)).length;
}

/**
 * The row of cards the gap sits in, counted from 1 as a reader sees them: a new row starts where a
 * card begins below every card before it.
 */
function bandOf(cards, row) {
    let band = 0;
    let bottom = -1;

    [...cards].sort((a, b) => a.row - b.row).forEach((card) => {
        if (card.row > row) return;

        if (card.row >= bottom) band += 1;

        bottom = Math.max(bottom, card.row + card.rows);
    });

    return Math.max(1, band);
}

/** The card the gap sits next to: the one on its left, else the one above it, else the card before it. */
function neighbourOf(layout, gap) {
    const cells = occupancy(layout.cards);
    const left = gap.column > 0 ? cells[gap.row]?.[gap.column - 1] : null;
    const above = gap.row > 0 ? cells[gap.row - 1]?.[gap.column] : null;
    const before = layout.cards[insertIndex(layout.cards, gap) - 1] ?? null;

    return (left ?? above ?? before)?.id ?? null;
}
